import { EventSignatureSummary, EventTypeBackend } from '../../../../types/events.types';
type LinkedSignatureRow = {
  docusignFileLinks?: string[];
  filesLinks?: string[];
  id?: string;
  linkedSignatureFileLink?: string;
  linkedSignatureId?: string;
  signatureStatus?: string;
};

type LinkedSignatureEventSource = {
  conversion?: { items: LinkedSignatureRow[] };
  docusignFileLinks?: string[];
  exercise?: { items: LinkedSignatureRow[] };
  filesLinks?: string[];
  id?: string;
  items?: LinkedSignatureRow[];
  signatureSummary?: EventSignatureSummary;
  type: EventTypeBackend;
};

const getRowByTransactionId = (
  event: LinkedSignatureEventSource,
  transactionId: string,
): LinkedSignatureRow | undefined =>
  getLinkedSignatureRows(event).find((row) => row.id === transactionId);

const collectDocusignLinksFromRow = (row: LinkedSignatureRow): string[] => {
  if (row.docusignFileLinks?.length) {
    return row.docusignFileLinks.filter(Boolean);
  }
  return row.linkedSignatureFileLink ? [row.linkedSignatureFileLink] : [];
};

const collectFromRows = (rows: LinkedSignatureRow[], transactionId?: string): string[] =>
  rows
    .filter((row) => (!transactionId ? true : row.id === transactionId))
    .flatMap((row) => collectDocusignLinksFromRow(row));

const getLinkedSignatureRows = (event: LinkedSignatureEventSource): LinkedSignatureRow[] => {
  switch (event.type) {
    case EventTypeBackend.APPROVAL:
    case EventTypeBackend.EXERCISE:
    case EventTypeBackend.PAYOUT:
      return event.exercise?.items ?? [];
    case EventTypeBackend.BUYBACK:
    case EventTypeBackend.FUNDRAISING_ROUND:
    case EventTypeBackend.GRANT:
    case EventTypeBackend.SECONDARIES:
    case EventTypeBackend.SHARE_ISSUANCE:
      return event.items ?? [];
    case EventTypeBackend.SAFE:
      return event.id
        ? [
            {
              id: event.id,
              filesLinks: event.filesLinks,
              docusignFileLinks: event.docusignFileLinks,
              linkedSignatureFileLink: event.signatureSummary?.linkedSignatureFileLink,
              signatureStatus: event.signatureSummary?.signatureStatus,
            },
          ]
        : [];
    case EventTypeBackend.SAFE_CONVERSION:
      return [];
    default:
      return [];
  }
};

export const excludeDocusignFileLinks = (
  filesLinks: string[],
  docusignLinks: string[],
): string[] => {
  if (docusignLinks.length === 0) return filesLinks;

  const exclude = new Set(docusignLinks);
  return filesLinks.filter((link) => !exclude.has(link));
};

export const collectLinkedSignatureFileLinks = (
  event: LinkedSignatureEventSource | null,
  options?: { transactionId?: string },
): string[] => {
  if (!event) return [];

  if (options && !options.transactionId) return [];

  const links = collectFromRows(getLinkedSignatureRows(event), options?.transactionId);
  return Array.from(new Set(links));
};

const rowHasLinkedSignature = (row: LinkedSignatureRow | undefined): boolean =>
  Boolean(row?.linkedSignatureId || row?.signatureStatus);

const collectItemOwnedDocusignFileLinks = (event: LinkedSignatureEventSource): Set<string> => {
  const links = new Set<string>();

  for (const row of getLinkedSignatureRows(event)) {
    const rowLinks = collectDocusignLinksFromRow(row);
    if (rowLinks.length === 0) continue;

    if (!rowHasLinkedSignature(row) && !row.docusignFileLinks?.length) continue;

    for (const link of rowLinks) {
      links.add(link);
    }
  }

  return links;
};

export const resolveEventGeneralLinkedSignatureFileLinks = (
  event: LinkedSignatureEventSource | null,
): string[] => {
  if (!event) return [];

  const itemOwnedDocusignLinks = collectItemOwnedDocusignFileLinks(event);
  const eventLevelDocusign = (event.docusignFileLinks ?? []).filter(
    (link) => !itemOwnedDocusignLinks.has(link),
  );
  const summaryLink = event.signatureSummary?.linkedSignatureFileLink;
  const fromSummary = summaryLink && !eventLevelDocusign.includes(summaryLink) ? [summaryLink] : [];

  return Array.from(new Set([...eventLevelDocusign, ...fromSummary].filter(Boolean)));
};

/** Event-level QuickSign links only (multiple allowed); wrap */
export const resolveEventLevelDocusignFileLinks = (
  event: LinkedSignatureEventSource | null,
): string[] => resolveEventGeneralLinkedSignatureFileLinks(event);

export const resolveEventAllDocusignFileLinks = (
  event: LinkedSignatureEventSource | null,
): string[] => {
  if (!event) return [];

  const fromRows = collectLinkedSignatureFileLinks(event);
  const fromItems = Array.from(collectItemOwnedDocusignFileLinks(event));
  const fromGeneral = resolveEventGeneralLinkedSignatureFileLinks(event);
  const fromEvent = event.docusignFileLinks ?? [];

  return Array.from(
    new Set([...fromRows, ...fromItems, ...fromGeneral, ...fromEvent].filter(Boolean)),
  );
};

const linkPathKey = (link: string): string => {
  try {
    return decodeURIComponent(new URL(link).pathname).toLowerCase();
  } catch {
    return decodeURIComponent(link.split('?')[0]).toLowerCase();
  }
};

export const resolveDisplayedDocusignFileLinks = (
  filesLinks: string[],
  candidateDocusignLinks: string[],
): string[] => {
  const candidates = candidateDocusignLinks.filter(Boolean);
  if (candidates.length === 0) return [];

  const exactSet = new Set(candidates);
  const pathKeys = new Set(candidates.map(linkPathKey));

  return filesLinks.filter((link) => {
    if (exactSet.has(link)) return true;
    return pathKeys.has(linkPathKey(link));
  });
};

export const resolveEventLevelUploadDocusignFileLinks = (
  event: LinkedSignatureEventSource | null,
  knownFileLinks: string[] = [],
): string[] => {
  const candidateDocusignLinks = resolveEventLevelDocusignFileLinks(event);

  return Array.from(
    new Set(
      [
        ...candidateDocusignLinks,
        ...resolveDisplayedDocusignFileLinks(knownFileLinks, candidateDocusignLinks),
      ].filter(Boolean),
    ),
  );
};

export const isDocusignFileLink = (link: string, candidateDocusignLinks: string[]): boolean =>
  resolveDisplayedDocusignFileLinks([link], candidateDocusignLinks).length > 0;

export const resolveEventGeneralManualFileLinks = (
  event: LinkedSignatureEventSource | null,
  filesLinks?: string[],
): string[] => {
  const source = filesLinks ?? event?.filesLinks ?? [];
  const candidateDocusignLinks = resolveEventAllDocusignFileLinks(event);

  return source.filter((link) => !isDocusignFileLink(link, candidateDocusignLinks));
};

export const resolveEventGeneralDocumentFileLinks = (
  event: LinkedSignatureEventSource | null,
): string[] => {
  const docusignLinks = resolveEventAllDocusignFileLinks(event);
  const manualLinks = resolveEventGeneralManualFileLinks(event);

  return Array.from(new Set([...manualLinks, ...docusignLinks].filter(Boolean)));
};

export const resolveTransactionLinkedSignatureFileLink = (
  event: LinkedSignatureEventSource | null,
  transactionId?: string,
): string | undefined => {
  if (!event || !transactionId) return undefined;

  const row = getRowByTransactionId(event, transactionId);
  const link = row?.linkedSignatureFileLink;
  if (!link) return undefined;

  if (!row.linkedSignatureId) return undefined;

  const eventGeneralLinks = resolveEventGeneralLinkedSignatureFileLinks(event);
  if (eventGeneralLinks.includes(link) && !(row.filesLinks ?? []).includes(link)) {
    return undefined;
  }

  return link;
};

export const resolveTransactionLinkedSignatureFileLinks = (
  event: LinkedSignatureEventSource | null,
  transactionId?: string,
): string[] => {
  if (!event || !transactionId) return [];

  const row = getRowByTransactionId(event, transactionId);
  if (!rowHasLinkedSignature(row)) return [];

  if (row?.docusignFileLinks?.length) {
    return Array.from(new Set(row.docusignFileLinks.filter(Boolean)));
  }

  const link = resolveTransactionLinkedSignatureFileLink(event, transactionId);
  return link ? [link] : [];
};

export const resolveTransactionItemDocusignFileLinks = (
  event: LinkedSignatureEventSource | null,
  transactionId?: string,
): string[] =>
  Array.from(
    new Set(
      [
        ...resolveTransactionLinkedSignatureFileLinks(event, transactionId),
        ...collectLinkedSignatureFileLinks(event, { transactionId }),
      ].filter(Boolean),
    ),
  );

export const resolveTransactionManualFileLinks = (
  event: LinkedSignatureEventSource | null,
  transactionId: string | undefined,
  filesLinks: string[],
): string[] => {
  if (!event) return filesLinks;

  const row = transactionId ? getRowByTransactionId(event, transactionId) : undefined;
  const docusignLinks = [
    ...resolveTransactionDocusignFileLinks(event, transactionId),
    ...(row?.docusignFileLinks ?? []),
    ...(row?.linkedSignatureFileLink ? [row.linkedSignatureFileLink] : []),
  ];

  return excludeDocusignFileLinks(filesLinks, docusignLinks);
};

export const resolveTransactionDocusignFileLinks = (
  event: LinkedSignatureEventSource | null,
  transactionId?: string,
): string[] => {
  if (!event || !transactionId) return [];

  return Array.from(
    new Set(
      [
        ...resolveTransactionLinkedSignatureFileLinks(event, transactionId),
        ...collectLinkedSignatureFileLinks(event, { transactionId }),
        ...resolveEventLevelDocusignFileLinks(event),
      ].filter(Boolean),
    ),
  );
};

/** Persist manual uploads only; DocuSign PDFs are owned by signature records and vault sync. */
export const mergeTransactionFileLinksForSave = (
  event: LinkedSignatureEventSource | null,
  transactionId: string | undefined,
  manualFileLinks: string[],
): string[] => {
  if (!event) {
    return Array.from(new Set(manualFileLinks.filter(Boolean)));
  }

  const row = transactionId ? getRowByTransactionId(event, transactionId) : undefined;
  const docusignLinks = [
    ...resolveTransactionDocusignFileLinks(event, transactionId),
    ...(row?.docusignFileLinks ?? []),
    ...(row?.linkedSignatureFileLink ? [row.linkedSignatureFileLink] : []),
  ];

  return Array.from(
    new Set(
      manualFileLinks.filter((link) => !isDocusignFileLink(link, docusignLinks)).filter(Boolean),
    ),
  );
};

export const mergeEventGeneralFileLinksForSave = (
  event: LinkedSignatureEventSource | null,
  manualFileLinks: string[],
): string[] => {
  const docusignLinks = resolveEventAllDocusignFileLinks(event);

  return Array.from(
    new Set(
      manualFileLinks.filter((link) => !isDocusignFileLink(link, docusignLinks)).filter(Boolean),
    ),
  );
};

export const resolveExerciseFamilyManualFileLinks = (
  event: LinkedSignatureEventSource | null,
  filesLinks?: string[],
): string[] => {
  const source = filesLinks ?? event?.filesLinks ?? [];
  const candidateDocusignLinks = resolveEventAllDocusignFileLinks(event);

  return source.filter((link) => !isDocusignFileLink(link, candidateDocusignLinks));
};

export const mergeExerciseFamilyFileLinksForSave = (
  event: LinkedSignatureEventSource | null,
  manualFileLinks: string[],
): string[] => {
  const docusignLinks = resolveEventAllDocusignFileLinks(event);

  return Array.from(
    new Set(
      manualFileLinks.filter((link) => !isDocusignFileLink(link, docusignLinks)).filter(Boolean),
    ),
  );
};

export const resolveManualFileLinksExcludingDocusign = (
  filesLinks: string[],
  docusignFileLinks: string[],
): string[] => filesLinks.filter((link) => !isDocusignFileLink(link, docusignFileLinks));

export const mergeFileLinksWithDocusignForSave = (
  manualFileLinks: string[],
  docusignFileLinks: string[],
): string[] => {
  const docusignLinks = docusignFileLinks.filter(Boolean);
  const links = [
    ...docusignLinks,
    ...manualFileLinks.filter((link) => !isDocusignFileLink(link, docusignLinks)),
  ];

  return Array.from(new Set(links.filter(Boolean)));
};
